/* eslint-env node */
/* global Intl */

const hasValue = value =>
  value !== undefined &&
  value !== null &&
  !(typeof value === "string" && value.trim() === "");

const escapeHtml = value =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const labelFromKey = key =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, character => character.toUpperCase());

const changeLabels = {
  Address: "Address",
  Cancellation: "Cancellation",
  LicensingAuthority: "Licensing Authority",
  Other: "Other",
  Registration: "Registration",
  Use: "Use",
  Vehicle: "Vehicle"
};

const formatDate = (value, includeTime = false) => {
  if (!hasValue(value)) return "";

  const dateString = String(value);
  const calendarDate = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateString);
  const displayTimeZone = calendarDate && !hasTimezone && includeTime
    ? "UTC"
    : "Europe/London";
  const date =
    calendarDate && !hasTimezone
      ? new Date(
          Date.UTC(
            Number(calendarDate[1]),
            Number(calendarDate[2]) - 1,
            Number(calendarDate[3]),
            ...(includeTime
              ? (dateString.match(/T(\d{2}):(\d{2})/) || [])
                  .slice(1)
                  .map(Number)
              : [])
          )
        )
      : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: displayTimeZone
  }).format(date);

  if (!includeTime) return datePart;

  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: displayTimeZone
  }).format(date);

  return `${datePart} at ${timePart}`;
};

const isCurrencyKey = key =>
  /value|amount|premium|cost|price|fee|excess|sum insured|limit/i.test(key);

const formatCurrency = value =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);

const formatAddress = address =>
  [
    address.house || address.houseNo,
    address.street,
    address.city,
    address.county,
    address.postcode
  ]
    .filter(hasValue)
    .map(String)
    .join(", ");

const formatValue = (key, value) => {
  if (!hasValue(value)) return "";

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "number" && isCurrencyKey(key)) {
    return formatCurrency(value);
  }

  if (typeof value === "string") {
    if (/date|effective|purchase|accident/i.test(key)) {
      return formatDate(value);
    }
    if (isCurrencyKey(key) && /^\s*-?\d+(\.\d+)?\s*$/.test(value)) {
      return formatCurrency(Number(value));
    }
    return value;
  }

  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "object") {
    if (/address/i.test(key)) return formatAddress(value);
    return Object.values(value)
      .map(String)
      .join(", ");
  }
  return String(value);
};

const renderRow = (label, value) => {
  if (!hasValue(value)) return "";
  return `<tr><td style="width:34%; padding:10px; border:1px solid #d5d5d5; background-color:#f2f2f2; font-weight:bold; vertical-align:top;">${escapeHtml(
    label
  )}</td><td style="padding:10px; border:1px solid #d5d5d5; vertical-align:top; word-break:break-word;">${escapeHtml(
    value
  )}</td></tr>`;
};

const renderSectionHeader = title =>
  `<tr><td colspan="2" style="padding:12px; background-color:#000000; color:#ffffff; font-size:16px; font-weight:bold;">${escapeHtml(
    title
  )}</td></tr>`;

const renderCaseDetails = request => {
  const rows = [
    renderRow(
      "Policy number",
      formatValue("policyNumber", request.policyNumber)
    ),
    renderRow("Origin", formatValue("Origin", request.Origin)),
    renderRow("Case type", formatValue("Type", request.Type)),
    renderRow(
      "Reason",
      formatValue(
        "reasonForCase",
        request.reasonForCase || request.Reason_for_case__c
      )
    ),
    renderRow("Date to action", formatDate(request.dateToAction, true)),
    renderRow("Comments", formatValue("Comments", request.Comments))
  ].join("");

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse; font-family:Arial, Helvetica, sans-serif; font-size:14px; color:#111111;">${renderSectionHeader(
    "Case details"
  )}${rows}</table>`;
};

const renderChange = ([changeKey, change]) => {
  if (!change || change.Requested !== true) return "";

  const rows = Object.entries(change.Details || {})
    .filter(([, value]) => hasValue(value))
    .map(([key, value]) =>
      renderRow(labelFromKey(key), formatValue(key, value))
    )
    .join("");
  const effectiveFrom = hasValue(change.effectiveFrom)
    ? `<tr><td colspan="2" style="padding:14px 0 12px; font-size:14px;"><strong>Effective from:</strong> ${escapeHtml(
        formatDate(change.effectiveFrom, true)
      )}</td></tr>`
    : "";

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse; margin-top:28px; font-family:Arial, Helvetica, sans-serif; font-size:14px; color:#111111;">${renderSectionHeader(
    `Requested change: ${changeLabels[changeKey] || labelFromKey(changeKey)}`
  )}${effectiveFrom}${rows}</table>`;
};

const renderSupportEmail = (request = {}) => {
  const changes =
    request.Changes && typeof request.Changes === "object"
      ? request.Changes
      : {};
  const requestedChanges = Object.entries(changes)
    .map(renderChange)
    .join("");

  return `<!doctype html><html><body style="margin:0; padding:0; background-color:#f2f2f2; font-family:Arial, Helvetica, sans-serif;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse; background-color:#f2f2f2;"><tr><td align="center" style="padding:15px;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%; max-width:700px; border-collapse:collapse; background-color:#ffffff;"><tr><td style="padding:28px 24px; background-color:#000000; color:#ffffff;"><h1 style="margin:0 0 10px; font-family:Arial, Helvetica, sans-serif; font-size:24px; line-height:1.2; color:#ffffff;">New MTA Request</h1><p style="margin:0; font-family:Arial, Helvetica, sans-serif; font-size:14px; line-height:1.5; color:#ffffff;">A customer has submitted a request to make a change to their policy.</p></td></tr><tr><td style="padding:24px;">${renderCaseDetails(
    request
  )}${requestedChanges}</td></tr></table></td></tr></table></body></html>`;
};

const renderSupportText = (request = {}) => {
  const lines = [
    "New MTA Request",
    "A customer has submitted a request to make a change to their policy.",
    "",
    "Case details",
    `Policy number: ${formatValue("policyNumber", request.policyNumber)}`,
    `Origin: ${formatValue("Origin", request.Origin)}`,
    `Case type: ${formatValue("Type", request.Type)}`,
    `Reason: ${formatValue(
      "reasonForCase",
      request.reasonForCase || request.Reason_for_case__c
    )}`,
    `Date to action: ${formatDate(request.dateToAction, true)}`
  ];

  if (hasValue(request.Comments)) lines.push(`Comments: ${request.Comments}`);

  Object.entries(request.Changes || {}).forEach(([key, change]) => {
    if (!change || change.Requested !== true) return;
    lines.push(
      "",
      `Requested change: ${changeLabels[key] || labelFromKey(key)}`
    );
    if (hasValue(change.effectiveFrom)) {
      lines.push(`Effective from: ${formatDate(change.effectiveFrom, true)}`);
    }
    Object.entries(change.Details || {}).forEach(([detailKey, value]) => {
      if (hasValue(value))
        lines.push(
          `${labelFromKey(detailKey)}: ${formatValue(detailKey, value)}`
        );
    });
  });

  return lines.filter(line => line !== undefined).join("\n");
};

module.exports = { renderSupportEmail, renderSupportText };
